import { useTranslation } from 'react-i18next'

import { getGarageParkingPeriod } from '../garage-parking-period'
import { Currency } from '../types'
import { useGetGarageAvailability, useGetGaragePrice } from '../pwa/hooks/use-garage-api'

export function useGarageBookingDetails(garageId: string | number | undefined, currency: Currency) {
    const { t } = useTranslation()
    const availability = useGetGarageAvailability(garageId)
    const priceQuery = useGetGaragePrice(garageId)
    const priceResponse = priceQuery.data
    const price = priceQuery.status === 'success' && priceResponse && !('errors' in priceResponse) ? priceResponse : undefined
    const parkingPeriod = price?.garage_id === Number(garageId)
        ? getGarageParkingPeriod({
            beginsAt: price.begins_at,
            endsAt: price.ends_at,
            minimumStayApplied: price.minimum_stay_applied,
        })
        : null
    const amount = currency === Currency.eur ? price?.amount_cents_eur : price?.amount_cents_czk
    const hourlyAmount = currency === Currency.eur ? price?.amount_cents_hourly_eur : price?.amount_cents_hourly_czk
    const hasPrice = parkingPeriod !== null && typeof amount === 'number' && Number.isFinite(amount) && amount >= 0
    const priceErrors = priceResponse && 'errors' in priceResponse ? priceResponse.errors : []
    const errors = [...new Set([
        ...(availability.status === 'success' ? availability.data?.errors ?? [] : []),
        ...priceErrors,
        ...(availability.status === 'error' ? [t('minimumStayPrice.availabilityUnavailable')] : []),
        ...(priceQuery.status === 'error' || (priceQuery.status === 'success' && !hasPrice && priceErrors.length === 0)
            ? [t('minimumStayPrice.unavailable')]
            : []),
    ])]
    const loading = availability.status === 'pending' || priceQuery.status === 'pending' ||
        availability.isFetching || priceQuery.isFetching
    const quoteReady = hasPrice && !loading
    const minimumStayApplied = quoteReady && price?.minimum_stay_applied === true

    return {
        amountCents: quoteReady ? amount : null,
        hourlyAmountCents: quoteReady && typeof hourlyAmount === 'number' && Number.isFinite(hourlyAmount) ? hourlyAmount : null,
        parkingPeriod: quoteReady ? parkingPeriod : null,
        effectiveEndsAt: quoteReady ? price?.ends_at ?? null : null,
        minimumStayApplied,
        showMinimumStay: minimumStayApplied && availability.status === 'success' &&
            availability.data.time_range_valid === true && availability.data.available === true,
        isUnavailable: availability.status === 'success' &&
            availability.data.time_range_valid === true && availability.data.available === false,
        isTimeRangeInvalid: availability.status === 'success' && availability.data.time_range_valid === false,
        loading,
        disabled: loading || availability.data?.available !== true || !hasPrice || errors.length > 0,
        errors,
    }
}

export type GarageBookingDetails = ReturnType<typeof useGarageBookingDetails>
